import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import { filterSettings } from '../components/settings/settingsCatalog';
import { ChevronIcon, SearchIcon } from '../components/settings/SettingsIcons';
import '../styles/OrgAdminSettings.css';

// The settings index: grouped rows, one tap to a full screen.
//
// Replaces a single page that stacked every panel vertically. That page worked
// while there were two of them; at ten it meant scrolling past the auditor
// directory to reach the work week. A menu costs one extra tap and gives back
// the thing a long settings page never has — the ability to see everything
// available at a glance.
//
// The rows themselves come from settingsCatalog, which the detail route also
// reads, so a row can never point at a screen that doesn't exist.

function OrgAdminSettings() {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  // Permission-filtered as well as searched — a row this user cannot open must
  // not be offered, or Invoicing 403s the moment they tap it.
  const groups = useMemo(() => filterSettings(query, user), [query, user]);

  return (
    <OrgAdminLayout title="Settings" subtitle="Manage your practice, your clients and your account">
      <div className="set-index">
        <div className="set-search">
          <span className="set-search-icon"><SearchIcon /></span>
          <input
            type="search"
            className="set-search-input"
            placeholder="Search settings"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search settings"
          />
        </div>

        {groups.length === 0 && (
          <p className="set-empty">
            Nothing matches “{query}”.
          </p>
        )}

        {groups.map((group) => (
          <section className="set-group" key={group.id}>
            <h2 className="set-group-title">{group.title}</h2>
            <div className="set-list">
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.slug}
                    to={`/org-admin/settings/${item.slug}`}
                    className="set-row"
                  >
                    <span className="set-row-icon"><Icon /></span>
                    <span className="set-row-text">
                      <span className="set-row-label">{item.label}</span>
                      <span className="set-row-desc">{item.description}</span>
                    </span>
                    <span className="set-row-chevron"><ChevronIcon /></span>
                  </Link>
                );
              })}
            </div>
            {/* Under the group, not over it: the caption explains what you just
                read rather than making you read a preamble first. */}
            {group.caption && !query && <p className="set-group-caption">{group.caption}</p>}
          </section>
        ))}
      </div>
    </OrgAdminLayout>
  );
}

export default OrgAdminSettings;
